import React, { useCallback, useEffect, useState } from "react";
import { Eye, EyeOff, Pencil, Trash2 } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { deleteBlog, getBlogs, toggleBlogStatus } from "../../services/servicesblogs";
import "./BlogPosts.css";

const EDIT_BLOG_STORAGE_KEY = "content-manager-edit-blog";
const PAGE_SIZE = 10;

// Blog list page for browsing and managing all posts.
export default function BlogPosts() {
  const navigate = useNavigate();
  const location = useLocation();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeToggleId, setActiveToggleId] = useState(null);
  const [latestBlogConsumed, setLatestBlogConsumed] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Fetches blogs and merges in the latest saved blog passed through navigation state.
  const fetchBlogs = useCallback(async (pageToLoad = 1) => {
    try {
      const response = await getBlogs({ page: pageToLoad, limit: PAGE_SIZE });
      const payload = response?.data ?? response ?? {};
      const blogsData = Array.isArray(response?.data?.blogs)
        ? response.data.blogs
        : Array.isArray(response?.blogs)
          ? response.blogs
          : Array.isArray(response?.data?.data?.blogs)
            ? response.data.data.blogs
            : [];
      const nextTotalPages = Number(
        payload?.totalPages ?? response?.data?.data?.totalPages ?? 1
      );
      const safeTotalPages = Number.isFinite(nextTotalPages) && nextTotalPages > 0 ? nextTotalPages : 1;
      setTotalPages(safeTotalPages);

      if (blogsData.length === 0 && pageToLoad > 1 && safeTotalPages < pageToLoad) {
        setCurrentPage(safeTotalPages);
        return;
      }

      const latestBlog = location.state?.latestBlog;
      if (latestBlog && !latestBlogConsumed && pageToLoad === 1) {
        const latestId = latestBlog.id || latestBlog._id;
        const merged = blogsData.some((blog) => (blog.id || blog._id) === latestId)
          ? blogsData.map((blog) =>
              (blog.id || blog._id) === latestId ? { ...blog, ...latestBlog } : blog
            )
          : [latestBlog, ...blogsData];
        setBlogs(merged);
        setLatestBlogConsumed(true);
      } else {
        setBlogs(blogsData);
      }
    } catch (error) {
      console.error("Error fetching blogs:", error);
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  }, [latestBlogConsumed, location.state?.latestBlog]);

  useEffect(() => {
    fetchBlogs(currentPage);
  }, [currentPage, fetchBlogs]);

  useEffect(() => {
    if (location.state?.latestBlog) {
      setLatestBlogConsumed(false);
      setCurrentPage(1);
    }
  }, [location.state?.latestBlog]);

  const getUpdatedDate = (blog) => {
    const rawValue =
      blog?.updatedAt ??
      blog?.updated_at ??
      blog?.updated ??
      blog?.lastUpdated ??
      blog?.modifiedAt ??
      blog?.createdAt;

    const resolvedValue =
      rawValue && typeof rawValue === "object"
        ? rawValue.$date ?? rawValue.date ?? rawValue.iso ?? null
        : rawValue;

    if (!resolvedValue) return "--";
    const date = new Date(resolvedValue);
    return Number.isNaN(date.getTime()) ? "--" : date.toLocaleDateString();
  };

  const handleToggleStatus = async (blogId) => {
    if (!blogId) {
      window.alert("Blog ID not found.");
      return;
    }

    try {
      setActiveToggleId(blogId);
      const response = await toggleBlogStatus(blogId);
      const updatedBlog =
        response?.data ??
        response?.blog ??
        response?.updatedBlog ??
        null;

      setBlogs((prevBlogs) =>
        prevBlogs.map((blog) =>
          (blog.id || blog._id) === blogId
            ? {
                ...blog,
                ...(updatedBlog || {}),
                status:
                  updatedBlog?.status ??
                  (String(blog.status).toUpperCase() === "PUBLISHED" ? "DRAFT" : "PUBLISHED"),
                publishedAt:
                  typeof updatedBlog?.publishedAt !== "undefined"
                    ? updatedBlog.publishedAt
                    : String(blog.status).toUpperCase() === "PUBLISHED"
                      ? null
                      : new Date().toISOString(),
              }
            : blog
        )
      );
    } catch (error) {
      window.alert(error?.response?.data?.message || "Failed to change blog status.");
      console.error("Error toggling blog status:", error);
    } finally {
      setActiveToggleId(null);
    }
  };

  const handleEdit = (blog) => {
    sessionStorage.setItem(EDIT_BLOG_STORAGE_KEY, JSON.stringify(blog));
    navigate("/ContentManager/ContentManagerLayout/blogposts/edit", { state: { blog } });
  };

  const handleViewDetails = (blog) => {
    navigate(`/ContentManager/ContentManagerLayout/blogposts/view/${blog.id || blog._id}`, {
      state: { blog },
    });
  };

  const handleDelete = async (id) => {
    try {
      await deleteBlog(id);
      await fetchBlogs(currentPage);
    } catch (error) {
      console.error("Error deleting blog:", error);
    }
  };

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  if (loading) {
    return <p className="loading">Loading blogs...</p>;
  }

  return (
    <div className="blogs-container">
      <h2 className="blogs-title">Blog Posts</h2>

      {blogs.length === 0 ? (
        <p>No blogs found</p>
      ) : (
        <>
          <table className="blogs-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Slug</th>
                <th>Status</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {blogs.map((blog) => (
                <tr key={blog.id || blog._id}>
                  <td className="title-cell">
                    <button type="button" className="blog-title-link" onClick={() => handleViewDetails(blog)}>
                      {blog.title}
                    </button>
                  </td>

                  <td>{blog.slug}</td>

                  <td>
                    <span className={`status ${blog.status?.toLowerCase()}`}>{blog.status}</span>
                  </td>

                  <td>{getUpdatedDate(blog)}</td>

                  <td className="actions">
                    <button
                      className="action-btn view"
                      onClick={() => handleToggleStatus(blog.id || blog._id)}
                      disabled={activeToggleId === (blog.id || blog._id)}
                    >
                      {String(blog.status).toUpperCase() === "PUBLISHED" ? (
                        <EyeOff size={16} />
                      ) : (
                        <Eye size={16} />
                      )}
                    </button>

                    <button className="action-btn edit" onClick={() => handleEdit(blog)}>
                      <Pencil size={16} />
                    </button>

                    <button
                      className="action-btn delete"
                      onClick={() => handleDelete(blog.id || blog._id)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="blogs-pagination">
            <span className="blogs-pagination-info">
              Page {currentPage} of {totalPages}
            </span>
            <div className="blogs-pagination-controls">
              <button
                type="button"
                className="pagination-btn"
                onClick={handlePreviousPage}
                disabled={currentPage <= 1}
              >
                Previous
              </button>
              <button
                type="button"
                className="pagination-btn"
                onClick={handleNextPage}
                disabled={currentPage >= totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
